import React from 'react'
import PropTypes from 'prop-types'
import inView from 'in-view'
import PromoBanner from './PromoBanner'
import { helpers, prismicHelpers } from '../../helpers'

/**
* PromoBanners components
*
* Displays promo banners. if no banners are present then nothing is rendered.
*
* The way banners are displayed depends on the `type` that is passed in:
* - `carousel` is for the hero carousel banners.
* - `default` is for generic banner(s).
*/
class PromoBanners extends React.Component {
  constructor(props) {
    super(props)

    this.uid = helpers.generateID('banners')
    this.selector = `#${this.uid}`

    this.state = {
      tracked: false,
    }

    this.trackBannerImpressions = this.trackBannerImpressions.bind(this)
    this.renderBanners = this.renderBanners.bind(this)
  }

  /**
  * Track banner impressions once the banners have entered the viewport.
  */
  componentDidMount() {
    if (!this.props.overrideVisibleCheck && !this.state.tracked) {
      inView(this.selector)
        .once('enter', this.trackBannerImpressions)
    }
  }

  componentDidUpdate() {
    if (this.props.overrideVisibleCheck && this.props.isVisible && !this.state.tracked) {
      this.trackBannerImpressions()
    }
  }

  /**
  * Grabs the current banners(s) and tracks banner impressions if appropriate.
  * Only tracks once per page load.
  *
  * @NOTE: This tracks impressions of banners currently visible to the user at
  * the time it is called. So if the user changes screen sizes after this and
  * new banners are displayed, they won't be recorded. There isn't really a
  * nice way of doing this without adding a lot of weight to it, and let's
  * face it, how often do people change the size of their viewport on any given
  * page? We'll probably miss out on  <0.05% of impressions so not worth our time.
  */
  trackBannerImpressions() {
    if (this.state.tracked) {
      return
    }
    const trackables = this.props.data.banners
      .filter(banner => prismicHelpers.currentlyVisible(banner))
      .map((banner, idx) => ({
        id: this.props.data.uid,
        name: this.props.data.promoName,
        creative: banner.creativeName,
        position: banner.promoPosition || `banner_${idx + 1}`, // start at 1 to make it more muggle friendly.
      }))

    if (!trackables.length) {
      return
    }

    $.track('promo impression', trackables)
    this.setState({
      tracked: true,
    })
  }

  /**
  * Renders individual PromoBanner components.
  */
  renderBanners() {
    return this.props.data.banners
      .filter(banner => prismicHelpers.userHasPermission(banner))
      .map((banner, idx) => {
        banner.promoId = this.props.data.uid
        banner.promoName = this.props.data.promoName
        banner.promoPosition = banner.promoPosition || `banner_${idx + 1}`

        return <PromoBanner promo={banner} key={idx} />
      })
  }

  render() {
    const viewableClass = prismicHelpers.formatViewClasses(this.props.data)


    const horizontalMarginClass = prismicHelpers.getSpacingClass(this.props.data, 'horizontalMargin')


    const verticalMarginClass = prismicHelpers.getSpacingClass(this.props.data, 'verticalMargin')


    const bannerSpacingClass = prismicHelpers.getSpacingClass(this.props.data, 'bannerSpacing')

    if (!this.props.data.banners.length || !prismicHelpers.userHasPermission(this.props.data)) {
      return false
    }

    return (
      <div id={this.uid} className={`${this.props.type}-banners ${viewableClass} ${horizontalMarginClass} ${verticalMarginClass} ${bannerSpacingClass}`}>
        { this.renderBanners() }
      </div>
    )
  }
}

PromoBanners.defaultProps = {
  data: {
    banners: '',
    uid: '',
    promoName: 'Generic Promo Banners',
  },
  type: 'default',
  overrideVisibleCheck: false,
  isVisible: true,
}

PromoBanners.propTypes = {
  data: PropTypes.object,
  type: PropTypes.string,
  overrideVisibleCheck: PropTypes.bool,
  isVisible: PropTypes.bool,
}

export default PromoBanners
